import { createReducer, on } from '@ngrx/store';
import * as actions from './actions';
import { CourseModule, CourseState, ModuleQuiz } from './models/state-models';
import { VideoConfig } from '../../shared/services/api/models/course-response';
import {
  checkForCompleteLevels,
  getNextActivity,
  updateCourseDataWithTraineeProgress,
} from './functions/reducer-functions';
import { TraineeAdapted } from '../../trainee/models/trainee-list.model';
import { APP_ROUTES } from '../../app.routes';

const initialState: CourseState = {
  traineeProgressId: '',
  culture: '',
  loading: false,
  userName: '',
  modules: [],
  currentModule: {} as CourseModule,
  currentVideo: {} as VideoConfig,
  videoPlayed: NaN,
  error: '',
  activityTracker: {
    redirect: '/home',
    text: 'Module is complete',
    canDownloadCertificate: false,
    certificateLevelId: NaN,
    type: '',
    showQuizRetakeButton: false,
  },
  certificates: [],
  progressResults: [],
  cookiePreferencesAccepted:
    localStorage.getItem('UnderstandingDyslexiaCookieConsent') === 'true',
  urlPath: {
    path: '',
    children: [],
  },
  traineeManagement: {
    traineeManagementInfoLoaded: false,
    canAccessTraineeManagement: false,
    traineeList: [],
    cacheTraineeList: [],
    selectedTraineeDetails: {} as TraineeAdapted,
    licensesRemaining: 0,
    totalLicenses: 0,
    otherEmails: [],
    levelOneId: NaN,
    levelTwoId: NaN,
    levelThreeId: NaN,
  },
};

export const courseDashboardReducer = createReducer<CourseState>(
  initialState,

  on(actions.DashboardPageActions.initApp, (): CourseState => {
    return {
      ...initialState,
      loading: true,
    };
  }),

  on(
    actions.DashboardPageActions.setCurrentPath,
    (state, action): CourseState => {
      return {
        ...state,
        currentModule:
          action.path === APP_ROUTES.TRAINEE
            ? state.modules[0]
            : state.currentModule,
        urlPath: {
          children: action.children,
          path: action.path,
        },
      };
    },
  ),

  on(
    actions.DashboardPageActions.moduleChange,
    (state, action): CourseState => {
      let moduleIndex =
        action.moduleIndex !== undefined
          ? action.moduleIndex
          : state.currentModule.moduleIndex;

      if (action.moduleIndex === undefined) {
        if (action.direction === -1) {
          moduleIndex = Math.max(0, state.currentModule.moduleIndex - 1);
        } else if (action.direction === 1) {
          moduleIndex = Math.min(
            state.modules.length - 1,
            state.currentModule.moduleIndex + 1,
          );
        }
      }

      const newCurrentModule = state.modules[moduleIndex];

      const updateActivityModuleChange = getNextActivity(
        newCurrentModule,
        initialState,
      );

      sessionStorage.setItem(
        'appSessionState',
        JSON.stringify({
          currentModuleIndex: newCurrentModule.moduleIndex,
          traineeProgressId: state.traineeProgressId,
        }),
      );

      return {
        ...state,
        currentModule: newCurrentModule,
        activityTracker: updateActivityModuleChange,
      };
    },
  ),

  on(
    actions.DashboardPageActions.moduleChangeViaMenu,
    (state, action): CourseState => {
      const newCurrentModule = state.modules[action.moduleIndex];

      const updateActivityModuleChange = getNextActivity(
        newCurrentModule,
        initialState,
      );

      sessionStorage.setItem(
        'appSessionState',
        JSON.stringify({
          currentModuleIndex: newCurrentModule.moduleIndex,
          traineeProgressId: state.traineeProgressId,
        }),
      );

      return {
        ...state,
        currentModule: newCurrentModule,
        activityTracker: updateActivityModuleChange,
      };
    },
  ),

  on(
    actions.DashboardPageActions.trainingVideoPlayed,
    (state, action): CourseState => {
      return {
        ...state,
        videoPlayed: action.videoId,
      };
    },
  ),

  on(
    actions.DashboardPageActions.activityTrackerDispatchedQuizRedirect,
    (state): CourseState => {
      return {
        ...state,
      };
    },
  ),

  on(
    actions.DashboardPageActions.userClickedPlayVideo,
    actions.DashboardPageActions.loadNextVideo,
    (state, action): CourseState => {
      return {
        ...state,
        currentVideo: {
          ...state.currentModule.videos[action.videoIndex],
          currentVideoIndex: action.videoIndex,
          isLastVideo:
            action.videoIndex + 1 >= state.currentModule.videos.length,
        },
      };
    },
  ),

  on(actions.DashboardPageActions.userClosedVideo, (state): CourseState => {
    return {
      ...state,
      currentVideo: {} as VideoConfig,
    };
  }),

  //***************************** API-DASHBOARD-ACTIONS *************************************************

  on(
    actions.DashboardApiActions.getCourseContentSuccess,
    (state, action): CourseState => {
      const courseDataWithProgress = updateCourseDataWithTraineeProgress(
        action.courseModules,
        state.progressResults,
      );

      let sessionStorageModuleIndex = null;
      if (
        action.sessionStorage &&
        state.traineeProgressId === action.sessionStorage.traineeProgressId
      ) {
        if (action.sessionStorage.currentModuleIndex >= 0)
          sessionStorageModuleIndex = action.sessionStorage.currentModuleIndex;
      } else {
        sessionStorage.clear();
      }

      const { updatedModules, certificates } = checkForCompleteLevels(
        courseDataWithProgress,
      );

      const nextAvailableModuleIndex = updatedModules.findIndex(
        (moduleItem) => moduleItem.moduleCompleted === false,
      );

      //* module is picked by checking if we have anything in storage first
      //* otherwise defaults to next uncompleted module
      //* if all modules are completed, defaults to first module

      const nextModuleIndex =
        sessionStorageModuleIndex !== null
          ? sessionStorageModuleIndex
          : nextAvailableModuleIndex !== -1
            ? nextAvailableModuleIndex
            : 0;

      const nextActivityTracker = getNextActivity(
        updatedModules[nextModuleIndex],
        initialState,
      );

      const [levelOneId, levelTwoId, levelThreeId] = action.courseLevels;

      return {
        ...state,
        activityTracker: nextActivityTracker,
        modules: updatedModules,
        currentModule: updatedModules[nextModuleIndex],
        certificates,
        traineeManagement: {
          ...state.traineeManagement,
          levelOneId,
          levelTwoId,
          levelThreeId,
        },
        loading: false,
      };
    },
  ),

  on(
    actions.DashboardApiActions.getTraineeProgressSuccess,
    (state, action): CourseState => {
      return {
        ...state,
        culture: action.culture,
        userName: action.userName,
        progressResults: action.progressResults,
        traineeProgressId: action.traineeProgressId,
        traineeManagement: {
          ...state.traineeManagement,
          traineeManagementInfoLoaded: true,
          canAccessTraineeManagement: action.canAccessTrainees,
        },
      };
    },
  ),

  on(
    actions.DashboardApiActions.getTraineeProgressFailed,
    (state, action): CourseState => {
      return {
        ...initialState,
        loading: false,
        error: action.error,
        culture: action.culture,
      };
    },
  ),

  on(
    actions.DashboardApiActions.getCourseContentFailed,
    actions.DashboardApiActions.videoUpdateFailed,
    (state, action): CourseState => {
      return {
        ...state,
        loading: false,
        error: action.error,
      };
    },
  ),

  //***************************** QUIZ-ACTIONS *************************************************

  on(actions.QuizPageActions.checkAnswer, (state, action): CourseState => {
    const updatedAnswers = state.currentModule.moduleQuiz.quizAnswers.map(
      (row, rIndex) =>
        row.map((item, cIndex) =>
          rIndex === state.currentModule.moduleQuiz.currentQuestionIndex &&
          cIndex === action.answerIndex
            ? { ...item, questionUsed: true }
            : item,
        ),
    );

    const hasUsedAnswers = updatedAnswers[
      state.currentModule.moduleQuiz.currentQuestionIndex
    ].some((item) => item.questionUsed);

    const currentAnswer =
      updatedAnswers[state.currentModule.moduleQuiz.currentQuestionIndex][
        action.answerIndex
      ];

    const canMoveToNextQuesiton = state.currentModule.moduleQuiz
      .canMoveToNextQuesiton
      ? true
      : currentAnswer.questionUsed && currentAnswer.valid;

    //- tracket wheher quiz can be completed
    const currentQuestionIndex = Math.min(
      state.currentModule.moduleQuiz.currentQuestionIndex + 1,
      state.currentModule.moduleQuiz.quizQuestions.length,
    );
    const submitQuizProgress =
      currentQuestionIndex ===
        state.currentModule.moduleQuiz.quizQuestions.length &&
      canMoveToNextQuesiton
        ? true
        : false;

    const moduleQuizUpdated: ModuleQuiz = {
      ...state.currentModule.moduleQuiz,
      quizAnswers: updatedAnswers,
      currentAnswerIndex: action.answerIndex,
      hasUsedAnswers,
      canMoveToNextQuesiton,
      submitQuizProgress,
    };

    const currentModuleUpdated = {
      ...state.currentModule,
      moduleQuiz: moduleQuizUpdated,
    };

    return {
      ...state,
      modules: state.modules.map((item) => {
        if (item.moduleId === currentModuleUpdated.moduleId) {
          return currentModuleUpdated;
        }
        return item;
      }),
      currentModule: currentModuleUpdated,
    };
  }),

  on(actions.QuizPageActions.nextQuestion, (state): CourseState => {
    const currentQuestionIndex = Math.min(
      state.currentModule.moduleQuiz.currentQuestionIndex + 1,
      state.currentModule.moduleQuiz.quizQuestions.length,
    );

    const hasUsedAnswers = state.currentModule.moduleQuiz.quizAnswers[
      currentQuestionIndex
    ].some((item) => item.questionUsed);

    const moduleQuizUpdated: ModuleQuiz = {
      ...state.currentModule.moduleQuiz,
      currentQuestionIndex,
      hasUsedAnswers,
      canMoveToNextQuesiton: false,
    };

    const currentModuleUpdated = {
      ...state.currentModule,
      moduleQuiz: moduleQuizUpdated,
    };

    return {
      ...state,
      modules: state.modules.map((item) => {
        if (item.moduleId === currentModuleUpdated.moduleId) {
          return currentModuleUpdated;
        }
        return item;
      }),
      currentModule: currentModuleUpdated,
    };
  }),

  on(
    actions.QuizPageActions.quizResetProgress,
    (state, action): CourseState => {
      const moduleQuizReset: ModuleQuiz = {
        ...state.currentModule.moduleQuiz,
        currentAnswerIndex: 0,
        canMoveToNextQuesiton: false,
        hasUsedAnswers: false,
        currentQuestionIndex: 0,
        quizComplete: false,
        submitQuizProgress: false,
        quizAnswers: state.currentModule.moduleQuiz.quizAnswers.map((item) =>
          item.map((innerItem) => ({
            ...innerItem,
            questionUsed: false,
          })),
        ),
      };

      return {
        ...state,
        modules: state.modules.map((item) => {
          if (
            item.moduleId === state.currentModule.moduleId &&
            action.resetQuizProgress
          ) {
            return {
              ...item,
              moduleQuiz: moduleQuizReset,
            };
          }
          return item;
        }),
        currentModule: {
          ...state.currentModule,
          moduleQuiz: action.resetQuizProgress
            ? moduleQuizReset
            : state.currentModule.moduleQuiz,
        },
      };
    },
  ),

  on(actions.QuizApiActions.quizUpdateFailed, (state, action): CourseState => {
    return {
      ...state,
      error: action.error,
    };
  }),

  //***************************** TRAINEE-ACTIONS *************************************************

  on(
    actions.TraineePageActions.filterTrainees,
    (state, action): CourseState => {
      const filteredTraineeList =
        action.searchFilter !== ''
          ? state.traineeManagement.cacheTraineeList.filter((trainee) =>
              trainee.name
                .replaceAll(' ', '')
                .toLowerCase()
                .includes(action.searchFilter),
            )
          : state.traineeManagement.cacheTraineeList;

      return {
        ...state,
        traineeManagement: {
          ...state.traineeManagement,
          traineeList: filteredTraineeList,
          selectedTraineeDetails: {} as TraineeAdapted,
        },
      };
    },
  ),

  on(actions.TraineePageActions.editTrainee, (state, action): CourseState => {
    return {
      ...state,
      traineeManagement: {
        ...state.traineeManagement,
        selectedTraineeDetails: action.trainee,
      },
    };
  }),

  on(
    actions.TraineePageActions.backToTraineeManagement,
    (state): CourseState => {
      return {
        ...state,
        traineeManagement: {
          ...state.traineeManagement,
          selectedTraineeDetails: {} as TraineeAdapted,
        },
      };
    },
  ),
  //***************************** API-TRAINEE-ACTIONS *************************************************

  on(
    actions.TraineeApiActions.getTraineeListSuccess,
    (state, action): CourseState => {
      const filteredTraineeList: TraineeAdapted[] =
        action.traineesOverview.udTrainees
          .filter((active) => !active.isDeleted)
          .map((trainee) => ({
            ...trainee,
            id: trainee.uid,
            name: trainee.firstName + ' ' + trainee.lastName,
          }));

      return {
        ...state,
        traineeManagement: {
          ...state.traineeManagement,
          ...action.traineesOverview,
          traineeList: filteredTraineeList,
          cacheTraineeList: filteredTraineeList,
          selectedTraineeDetails: {} as TraineeAdapted,
        },
      };
    },
  ),

  on(
    actions.TraineeApiActions.DownloadCertificateFailed,
    actions.TraineeApiActions.emailValidationFailed,
    actions.TraineeApiActions.inviteTraineeFailed,
    actions.TraineeApiActions.getTraineeListFailed,

    (state, action): CourseState => {
      return {
        ...state,
        loading: false,
        error: action.error,
      };
    },
  ),

  //***************************** COOKIES-ACTIONS *************************************************
  on(actions.DashboardPageActions.acceptCookies, (state): CourseState => {
    return {
      ...state,
      cookiePreferencesAccepted: true,
    };
  }),
);
